import { FC, useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterMatchMode } from 'primereact/api';

import { DataTableSelectionMultipleChangeEvent } from 'primereact/datatable';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useMutation } from '@tanstack/react-query';
import { getSettlement } from '@/services/getSettlement';

import { Container, Button, Table } from '@/components';
import { TableData } from '@/components/Table/types';
import ToolBar from '@/pages/MySettlement/Components/ToolBar/ToolBar';
import { Proforma } from '@/types/mySettlementType';

import styles from './MySettlement.module.scss';
import { TableFilters } from './Components/TableFiltersType';
import { getTableColumns } from './tableColumns';
import style from '../ContainerRelease/ContainerRelease.module.scss';
import ModalDeleted from './Components/ModalDeleted';
import Filters from './Components/Filters';

const today = new Date();
const sevenDaysAgo = new Date();
sevenDaysAgo.setDate(today.getDate() - 7);

const CONTAINER_REGEX = /^[^\d]*$/;

const initialFilters: TableFilters = {
  fechaDesde: sevenDaysAgo,
  fechaHasta: today,
  contenedor: '',
  estado: 'En Proceso',
  tipo: '',
  marca: '',
  numero: '',
};

const STATUSES_DISABLED_FOR_DELETION = [
  'Paga',
  'Facturada',
  'Agendada',
  'Pendiente de Pago',
];

export const tableFilters = {
  oid: { value: null, matchMode: FilterMatchMode.CONTAINS },
  tipo: { value: null, matchMode: FilterMatchMode.CONTAINS },
  observaciones: { value: null, matchMode: FilterMatchMode.CONTAINS },
  fechaInicio: { value: null, matchMode: FilterMatchMode.DATE_IS },
  cliente: { value: null, matchMode: FilterMatchMode.CONTAINS },
  rutCliente: { value: null, matchMode: FilterMatchMode.CONTAINS },
  estado: { value: null, matchMode: FilterMatchMode.CONTAINS },
};

const MySettlement: FC = () => {
  const { t } = useTranslation('mySettlement');
  const [filters, setFilters] = useState(initialFilters);
  const [selectedItems, setSelectedItems] = useState<Proforma[]>([]);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);

  const { fechaDesde, fechaHasta, estado, tipo } = filters;

  const containerIsValid = useMemo(() => {
    const { contenedor } = filters;
    if (!contenedor) return true;
    if (contenedor?.length !== 11) return false;
    const marca = contenedor.slice(0, 4);
    const numero = contenedor.slice(4);
    if (Number.isNaN(Number(numero)) || !CONTAINER_REGEX.test(marca))
      return false;

    return true;
  }, [filters]);

  const containerParts = useMemo(() => {
    const contenedor = filters?.contenedor;
    if (!containerIsValid || !contenedor)
      return { marca: '', numero: '', contenedor: '' };

    const marca = contenedor.slice(0, 4);
    const numero = contenedor.slice(4);
    return { marca, numero, contenedor };
  }, [filters?.contenedor, containerIsValid]);

  const {
    mutate: getData,
    data,
    isPending,
  } = useMutation({
    mutationFn: (params: TableFilters) =>
      getSettlement({ ...params, ...containerParts }),
    mutationKey: ['getSettlement'],
  });

  const handleSearchClick = useCallback(() => {
    getData(filters);
  }, [getData, filters]);

  useEffect(() => {
    getData({ fechaDesde, fechaHasta, estado, tipo });
  }, [fechaDesde, fechaHasta, estado, tipo, getData]);

  const onSelectionChange = (
    e: DataTableSelectionMultipleChangeEvent<Proforma[]>
  ) => {
    setSelectedItems(e.value);
  };

  const disabledForDeletion = (selectedItems || []).some(({ estado: status }) =>
    STATUSES_DISABLED_FOR_DELETION.includes(status)
  );

  const onDeletedModalClose = (isSuccess: boolean) => {
    setDeleteModalVisible(false);
    if (isSuccess) getData(filters);
  };

  return (
    <Container title={t('title')} className={styles.container}>
      <ToolBar />
      <br />
      <div className={styles.filtersContainer}>
        <div className={styles.filters}>
          <Filters filters={filters} setFilters={setFilters} />
        </div>
        <Button
          type="submit"
          className={styles.submitButton}
          onClick={handleSearchClick}
          disabled={!containerIsValid && (filters.contenedor?.length ?? 0) > 1}
        >
          <FontAwesomeIcon icon={faMagnifyingGlass} className={styles.icon} />
          {t('submitButtonText')}
        </Button>
      </div>

      <div className={style.secondaryActionsWrapper}>
        <div>
          <button
            disabled={!selectedItems?.length || disabledForDeletion}
            type="button"
            className={style.secondaryActionsButtons}
            onClick={() => setDeleteModalVisible(true)}
          >
            <FontAwesomeIcon icon={faTrash} />
            {t('delete')}
          </button>
        </div>
      </div>
      <div className={style.tableContainer}>
        <Table
          columns={getTableColumns(t)}
          data={data as unknown as TableData}
          isLoading={isPending}
          className={styles.table}
          filterDisplay="menu"
          selectedItems={selectedItems}
          onSelectionChange={onSelectionChange}
          selectionMode="checkbox"
          filters={tableFilters}
        />
      </div>
      <ModalDeleted
        oids={selectedItems?.map(({ oid }) => oid)}
        visible={deleteModalVisible}
        onClose={onDeletedModalClose}
      />
    </Container>
  );
};

export default MySettlement;
